import React, {Component} from 'react';
import PropTypes from "prop-types";
import {Button} from "../Button";

const stripHtml = (html) => {
    const temporalDivElement = document.createElement("div");
    temporalDivElement.innerHTML = html;

    const str = temporalDivElement.textContent || temporalDivElement.innerText || "";
    return str.replace(/\s+/g,' ').trim();
};

export default class InputForm extends Component {
    static propTypes = {
        /**
         * add icon to input
         */
        addon: PropTypes.oneOfType([
            PropTypes.array,
            PropTypes.object,
        ]),
        /**
         * input size (lg/sm)
         */
        size: PropTypes.string,
        /**
         * input background color (bg-gray/...)
         */
        background: PropTypes.string,
        /**
         * input placeholder
         */
        placeholder: PropTypes.string,
        /**
         * false - disable input border-radius, 'rounded' - make input 100% border-radius
         */
        inputBorderRadius: PropTypes.oneOfType([
            PropTypes.bool,
            PropTypes.string
        ]),
        /**
         * input type attribute (if you set radio, dont forget to define radio prop)
         */
        type: PropTypes.string,
        /**
         * input id attribute
         */
        id: PropTypes.string,
        /**
         * input name attribute
         */
        name: PropTypes.string,
        /**
         * input value attribute
         */
        value: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
            PropTypes.bool
        ]),
        /**
         * input autocomplete attribute
         */
        autoComplete: PropTypes.string,
        /**
         * form label (if you want put in label some html (link, or icon..) use children)
         */
        label: PropTypes.oneOfType([PropTypes.string,PropTypes.bool]),
        /**
         * disable resize for textarea
         */
        fixed: PropTypes.bool,
        /**
         * additional class for label
         */
        labelTop: PropTypes.bool,
        /**
         * array of objects with radio items
         */
        radio: PropTypes.oneOfType([
            PropTypes.object,
            PropTypes.array
        ]),
        /**
         * set radio items in 1 row
         */
        radioHorizontal: PropTypes.bool,
        /**
         * add clear button to input
         */
        clearButton: PropTypes.bool,
        /**
         * input on focus callback
         */
        onFocus: PropTypes.func,
        /**
         * input on keydown callback
         */
        onKeyDown: PropTypes.func,
        /**
         * input on change callback
         */
        onChange: PropTypes.func,
        /**
         * input on click callback
         */
        onClick: PropTypes.func,
        /**
         * input on key up callback
         */
        onKeyUp: PropTypes.func,
        /**
         * input on key up callback
         */
        onKeyPress: PropTypes.func,
        /**
         * input tooltip text
         */
        tooltip: PropTypes.string,
        /**
         * error messages what should be displayed in tooltip block
         */
        errorMessages: PropTypes.arrayOf(PropTypes.string),
        /**
         * prevent default change event, you should manage your own state
         */
        preventDefaultOnChange: PropTypes.bool,
        /**
         *  Component inner (can be anything), it replaces label (use this if you want put in label some html (link, or icon..))
         */
        children: PropTypes.oneOfType([
            PropTypes.arrayOf(PropTypes.node),
            PropTypes.node
        ])
    };

    static defaultProps = {
        addon: null,
        size: '',
        background: '',
        placeholder: '',
        inputBorderRadius: null,
        type: 'text',
        id: null,
        name: '',
        value: '',
        autoComplete: '',
        fixed: false,
        label: '',
        labelClass: '',
        wrapperClass: '',
        labelTop: false,
        radio: null,
        radioHorizontal: false,
        clearButton: false,
        onFocus: null,
        onKeyDown: null,
        onKeyUp: null,
        tooltip: '',
        errorMessages: [],
        preventDefaultOnChange: false
    };

    constructor(props){
        super(props);

        this.state = {
            value: '',
        };

        if(props.value !== undefined) {
            this.state.value = props.value;
        } else if(props.type === 'checkbox') {
            this.state.value = false;
        }

        this.onInputChanged = this.onInputChanged.bind(this);
        this.getValue = this.getValue.bind(this);
        this.setValue = this.setValue.bind(this);

        this.getInputEvents = this.getInputEvents.bind(this);
        this.getInputAttributes = this.getInputAttributes.bind(this);
        this.getInputClasses = this.getInputClasses.bind(this);
        this.getInputAddons = this.getInputAddons.bind(this);

        this.renderTextArea = this.renderTextArea.bind(this);
        this.renderCheckbox = this.renderCheckbox.bind(this);
        this.renderDefaultInput = this.renderDefaultInput.bind(this);
        this.renderRadio = this.renderRadio.bind(this);

        this.inputRef = React.createRef();
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if(prevProps.value !== this.props.value) {
            if (!this.props.preventDefaultOnChange) {
                this.setState({
                    value: this.props.value
                })
            }
        }
    }

    async onInputChanged(e) {
        e.persist();

        if (this.props.preventDefaultOnChange) {
            if(this.props.onChange) {
                this.props.onChange(e);
            }
            return;
        }

        await this.setState({
            value: e.target.type === 'checkbox' ? e.target.checked : e.target.value
        },()=>{
            if(this.props.onChange) {
                this.props.onChange(e);
            }
        });
    }

    getValue() {
        return this.state.value;
    }

    async setValue(n) {
        if(!isNaN(n) && typeof n !== "boolean") n = +n;
        await this.setState({
            value: n
        });
    }

    focus() {
        this.inputRef.focus();
    }

    getInputEvents() {
        const {
            onClick,
            onFocus,
            onKeyUp,
            onKeyDown,
            onBlur,
            onKeyPress
        } = this.props;

        return {
            onClick: onClick || null,
            onKeyUp: onKeyUp || null,
            onFocus: onFocus || null,
            onKeyDown: onKeyDown || null,
            onBlur: onBlur || null,
            onKeyPress: onKeyPress || null
        };
    }

    getInputAttributes() {
        const {
            name,
            id,
            placeholder,
            type,
            autoComplete,
            disabled
        } = this.props;

        return {
            name: name ? name : null,
            id: id ? id : null,
            placeholder: placeholder ? placeholder : null,
            type: type ? type : null,
            autoComplete: autoComplete ? autoComplete : null,
            disabled: disabled ? disabled : null,
            ref: (input) => this.inputRef = input
        };
    }

    getInputClasses() {
        const classes = ['form-group'];

        const {
            type,
            fixed,
            addon,
            size,
            background,
            labelTop,
            radio,
            inputBorderRadius,
            disabled,
            wrapperClass,
            className
        } = this.props;

        if(type === 'checkbox') {
            classes.push(`form--checkbox`);
        }

        if(fixed) {
            classes.push(`form--fixed`);
        }

        const getAddonClasses = (_addon) => {
            const addonClassNames = ['form-with-addon'];

            if(!Array.isArray(_addon)) {
                addonClassNames.push(`addon-${_addon.position}`);
                return addonClassNames;
            }

            for (const item of _addon) {
                addonClassNames.push(`addon-${item.position}`);
            }

            return addonClassNames;
        };

        if(addon) {
            classes.push(getAddonClasses(addon).join(' '));
        }
        if(size) {
            classes.push(`form-${size}`);
        }
        if(background) {
            classes.push(background);
        }
        if(labelTop) {
            classes.push('form-group--label-top');
        }
        if(radio && radio.length) {
            classes.push('form--radio');
        }
        if(inputBorderRadius === false) {
            classes.push('no-border-radius');
        } else if (inputBorderRadius === 'rounded') {
            classes.push('form-rounded');
        }
        if(disabled) {
            classes.push('form-group--disabled');
        }
        if(wrapperClass) {
            classes.push(wrapperClass);
        }
        if(className) {
            classes.push(className);
        }

        return classes;
    }

    getInputAddons() {
        const {
            addon
        } = this.props;
        const addons = [null, null];

        if(!addon) return addons;

        const setAddon = (item) => {
            const index = item.position === 'append' ? 1 : 0;
            if (item.button) {
                addons[index] = <Button type={"button"} {...item.button}/>;
            } else {
                addons[index] = <i className={item.icon}/>;
            }
        };

        if (Array.isArray(addon)) {
            for (const item of addon) {
                setAddon(item);
            }

            return addons;
        }

        setAddon(addon);

        return addons;
    }

    renderCheckbox(restOptions = {}) {
        const events = this.getInputEvents();
        const attrs = this.getInputAttributes();

        const {
            id,
            label,
            children,
            labelClass,
            preventDefaultOnChange
        } = this.props;

        return  (
            <label
                htmlFor={id}
                className={`${this.state.value ? 'checkbox--checked' : ''} ${!label && !children ? 'p-0 m-0' : ''} ${labelClass ? labelClass : ''}`}
            >
                {children || label || ''}
                <input
                    {...attrs}
                    {...events}
                    {...restOptions}
                    aria-label={label ? label : ''}
                    checked={preventDefaultOnChange ? this.props.value : this.state.value}
                    onChange={this.onInputChanged}
                    className={`form-control`}
                />
                <span className="checkmark"/>
            </label>
        )
    }

    renderTextArea(restOptions = {}) {
        const events = this.getInputEvents();
        const attrs = this.getInputAttributes();

        const {
            preventDefaultOnChange
        } = this.props;

        return (
            <textarea
                {...attrs}
                {...events}
                {...restOptions}
                value={preventDefaultOnChange ? this.props.value : this.state.value}
                onChange={this.onInputChanged}
                className={`form-control`}
            />
        )
    }

    renderRadio(restOptions = {}) {
        const attrs = this.getInputAttributes();
        const events = this.getInputEvents();

        const {
            radio,
            labelClass,
            radioHorizontal,
            disabled,
            preventDefaultOnChange
        } = this.props;

        if(!radio) {
            return this.renderDefaultInput();
        }

        const radioItems = [];
        if(Array.isArray(radio)) {
            for (const [index, item] of radio.entries()) {
                const radioContainerClassNames = ['radio-container'];

                if(labelClass) {
                    radioContainerClassNames.push(labelClass);
                }
                if(item.descr) {
                    radioContainerClassNames.push('radio--with-descr');
                }
                if(item.disabled) {
                    radioContainerClassNames.push('radio--disabled');
                }

                radioItems.push(
                    <label
                        key={index}
                        className={radioContainerClassNames.join(' ')}
                    >
                        <div className={`d-flex`}>
                            <span dangerouslySetInnerHTML={{__html:item.label}}/>
                        </div>
                        <input
                            type="radio"
                            {...attrs}
                            {...events}
                            {...restOptions}
                            checked={(preventDefaultOnChange ? this.props.value.toString() : this.state.value.toString()) === item.value.toString()}
                            aria-label={typeof item.label === 'string' ? stripHtml(item.label) : ''}
                            onChange={this.onInputChanged}
                            value={item.value}
                            disabled={typeof item.disabled !== "undefined" ? item.disabled : disabled}
                        />
                        <span className="checkmark"/>
                    </label>
                );
            }
        }

        return (
            <div className={`radio-group ${radioHorizontal ? 'radio-group--horizontal' : ''}`}>
                {radioItems}
            </div>
        )
    }

    renderDefaultInput(restOptions = {}) {
        const attrs = this.getInputAttributes();
        const events = this.getInputEvents();

        const {
            label,
            preventDefaultOnChange
        } = this.props;

        return (
            <input
                {...attrs}
                {...events}
                {...restOptions}
                value={preventDefaultOnChange ? this.props.value : this.state.value}
                onChange={this.onInputChanged}
                className={`form-control`}
                aria-label={label ? label : ''}
            />
        )
    }

    getClearButton() {
        const {
            type,
            clearButton,
        } =  this.props;

        return (clearButton && type !== 'checkbox' && type !== 'radio' && type !== 'textarea' ?
            <div className={'clear_input'} onClick={()=>this.setValue('')}>
                <i className="ic-close-black"/>
            </div> : null)
    }

    render() {
        const {
            type,
            label,
            clearButton,
            labelClass,
            children,
            tooltip,
            errorMessages,
            ...rest
        } =  this.props;

        const addons = this.getInputAddons();

        let inputBody;

        const reservedOptionNames = [
            "addon",
            "size",
            "background",
            "placeholder",
            "inputBorderRadius",
            "type",
            "id",
            "name",
            "value",
            "autoComplete",
            "fixed",
            "label",
            "labelClass",
            "wrapperClass",
            "labelTop",
            "radio",
            "radioHorizontal",
            "clearButton",
            "onClick",
            "onFocus",
            "onKeyUp",
            "onKeyDown",
            "onBlur",
            "onKeyPress",
            "tooltip",
            "errorMessages",
            "formChild",
            "preventDefaultOnChange"
        ];

        const restOptions = {};

        for (const [key, value] of Object.entries(rest)) {
            if(!reservedOptionNames.includes(key)) {
                restOptions[key] = value;
            }
        }

        switch(type) {
            case 'checkbox': inputBody = this.renderCheckbox(restOptions);
                break;
            case 'textarea': inputBody = this.renderTextArea(restOptions);
                break;
            case 'radio': inputBody = this.renderRadio(restOptions);
                break;
            default: inputBody = this.renderDefaultInput(restOptions);
        }

        return (
            <div className={this.getInputClasses().join(' ')}>
                {(label || children) && type !== 'checkbox' ? <label className={labelClass || ''}>
                    {children || label || ''}
                </label> : ''}
                <div className={'d-flex w-100'}>
                    {addons[0] ?
                        <div
                            className={`addon-prepend${addons[0].type === Button ? ' button-addon' : ''}`}
                        >
                            {addons[0]}
                        </div> : ''}
                    <div className={`input-wrapper${clearButton ? ' input-with-clear-button' : ''}`}>
                        {inputBody}
                        {this.getClearButton()}
                    </div>
                    {addons[1] ?
                        <div
                            className={`addon-append${addons[1].type === Button ? ' button-addon' : ''}`}
                        >
                            {addons[1]}
                        </div> : ''}
                </div>
            </div>
        )
    }
}
